import { useTranslation } from 'react-i18next'
import { Aurora, AuroraAssetGroup, AuroraAwhpData } from '../types'
import {
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material'
import { useMemo, useState } from 'react'
import { Add, History, Remove } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import AuroraTargetTemperatureInput from './AuroraTargetTemperatureInput'
import { formatTemperature, formatTemperatureDelta } from '../utils/string'
import { setAuroraHeatPumpTargetTemperature } from '../services/aurora'
import { useDispatch } from 'react-redux'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'

interface AuroraAssetGroupHeatPumpTemperatureControlProps {
  auroraAssetGroup: AuroraAssetGroup
  auroraAwhpData: Record<string, AuroraAwhpData>
  beforeSubmit?: (newTargetTemperature: number) => void
  afterSubmit?: (newTargetTemperature: number) => void
  onError?: (error: unknown) => void
}

export default function AuroraAssetGroupHeatPumpTargetTemperatureControl({
  auroraAssetGroup,
  auroraAwhpData,
  beforeSubmit,
  afterSubmit,
  onError
}: AuroraAssetGroupHeatPumpTemperatureControlProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The theme hook.
   */
  const theme = useTheme()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The auroras.
   */
  const auroras = auroraAssetGroup.assets
    .filter((asset) => asset.aurora !== undefined && asset.aurora !== null)
    .map(({ aurora, ...asset }) => ({ ...aurora, asset }) as Aurora)

  /**
   * The target temperature.
   */
  const targetTemperature = useMemo(() => {
    const values = Object.values(auroraAwhpData).map((reading) => reading.targets_zone1)

    if (values.length === 0) {
      return null
    }

    return values.reduce((sum, value) => sum + value, 0) / values.length
  }, [auroraAwhpData])

  /**
   * The room temperature.
   */
  const roomTemperature = useMemo(() => {
    const values = Object.values(auroraAwhpData).map((reading) => reading.temperature_room)

    if (values.length === 0) {
      return null
    }

    return values.reduce((sum, value) => sum + value, 0) / values.length
  }, [auroraAwhpData])

  /**
   * The new target temperature.
   */
  const [newTargetTemperature, setNewTargetTemperature] = useState<number | null>(null)

  const temperatureDelta =
    targetTemperature !== null && roomTemperature !== null
      ? roomTemperature - targetTemperature
      : null

  /**
   * The indicates if the submission is in progress.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * Increments the target temperature by 0.5 degrees.
   */
  function incrementNewTargetTemperature() {
    setNewTargetTemperature((prev) => {
      if (prev !== null) {
        return Math.min(prev + 0.5, 100)
      } else if (targetTemperature !== null) {
        return Math.min(targetTemperature + 0.5, 100)
      } else {
        return null
      }
    })
  }

  /**
   * Decrements the target temperature by 0.5 degrees.
   */
  function decrementNewTargetTemperature() {
    setNewTargetTemperature((prev) => {
      if (prev !== null) {
        return Math.max(prev - 0.5, 0)
      } else if (targetTemperature !== null) {
        return Math.max(targetTemperature - 0.5, 0)
      } else {
        return null
      }
    })
  }

  /**
   * Update the target temperature.
   */
  async function handleSubmit() {
    if (newTargetTemperature === null) {
      return
    }

    try {
      setSubmitting(true)

      beforeSubmit?.(newTargetTemperature)

      await Promise.all(
        auroras.map((aurora) => setAuroraHeatPumpTargetTemperature(aurora.id, newTargetTemperature))
      )
      setNewTargetTemperature(null)
      dispatch(createSuccessNotification(t('setHeatPumpTargetTemperatureSuccess')))

      afterSubmit?.(newTargetTemperature)
    } catch (error) {
      onError?.(error)
      dispatch(createErrorNotification(t('setHeatPumpTargetTemperatureError')))
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Reset the target temperature.
   */
  function handleReset() {
    setNewTargetTemperature(null)
  }

  if (auroras.length === 0) {
    return null
  }

  return (
    <FormGroup
      sx={{
        p: 4,
        borderRadius: 2,
        background: theme.palette.background.default
      }}
    >
      <FormControlLabel
        value="bottom"
        control={
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 1 }}
            gap={2}
          >
            <Stack direction="row" alignItems="center" gap={3}>
              {targetTemperature !== null && (
                <IconButton
                  color={newTargetTemperature !== null ? 'primary' : 'inherit'}
                  disabled={isSubmitting}
                  size="small"
                  sx={{ border: '2px solid' }}
                  onClick={decrementNewTargetTemperature}
                >
                  <Remove />
                </IconButton>
              )}

              <Stack direction="column" justifyContent="center" alignItems="center" gap={1}>
                {newTargetTemperature !== null ? (
                  <Typography variant="h2" color={isSubmitting ? 'text.disabled' : 'primary'}>
                    <AuroraTargetTemperatureInput
                      value={newTargetTemperature}
                      loading={isSubmitting}
                      onChange={setNewTargetTemperature}
                      onSubmit={handleSubmit}
                    />
                  </Typography>
                ) : targetTemperature !== null ? (
                  <Typography variant="h2" color="text.primary">
                    {formatTemperature(targetTemperature)}
                  </Typography>
                ) : (
                  <Skeleton variant="text" width="100px" sx={{ fontSize: '2.5rem' }} />
                )}

                <Typography color="text.secondary">
                  {`ΔT: ${temperatureDelta !== null ? formatTemperatureDelta(temperatureDelta) : '-'}`}
                </Typography>
              </Stack>

              {targetTemperature !== null && (
                <IconButton
                  color={newTargetTemperature !== null ? 'primary' : 'inherit'}
                  disabled={isSubmitting}
                  size="small"
                  sx={{
                    border: '2px solid'
                  }}
                  onClick={incrementNewTargetTemperature}
                >
                  <Add />
                </IconButton>
              )}
            </Stack>

            {newTargetTemperature !== null && (
              <Stack direction="row" alignItems="center" gap={2} sx={{ mr: '-48px' }}>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                  onClick={handleSubmit}
                >
                  {t('setNewTemperature')}
                </LoadingButton>

                <Tooltip title={t('restoreTargetTemperature')}>
                  <IconButton disabled={isSubmitting} onClick={handleReset}>
                    <History />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        }
        label={t('targetTemperature')}
        labelPlacement="top"
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          width: '100%',
          m: 0,
          justifyContent: 'space-between'
        }}
      />
    </FormGroup>
  )
}
